import { REGISTRATION_MODALS } from '@nerdwallet/js-auth-tools/dist/registration/constants';
import type { RegistrationModalType } from '@nerdwallet/js-auth-tools/dist/registration/constants';

import {
  IGNORABLE_V1,
  IGNORABLE_V3_ACCOUNT,
  IGNORABLE_V3_INSIGHTS,
  IGNORABLE_V3_INVESTING,
  IGNORABLE_V3_MORTGAGES,
  IGNORABLE_V3_RETIREMENT,
  IGNORABLE_V3_RIGHT_MOVE,
} from './analytics.constants';

type Variant = {
  id: string;
  regFlow?: RegistrationModalType;
  analyticsBase?: any;
  flow?: string;
};

export type IncludeExcludeConfigProps = {
  contentTypesToInclude?: string[];
  verticalsToInclude?: string[];
  topicsToInclude?: string[];
  urlsToInclude?: string[];
  urlMatch?: RegExp;
};

export type ExperimentConfig = {
  /**
   * ID of the experiment in Optimizely.
   * Recommended id format: {jira-1234}_{my-test-name}_experiment
   */
  id: string;
  variants: {
    /**
     * Control variant should be named "control", but in any case, should match your Optimizely config.
     */
    CONTROL: Variant;
    VARIANT?: Variant;
    VARIANTB?: Variant;
  };
  includeExcludeConfig?: IncludeExcludeConfigProps;
};

export const EXPERIMENTS: Record<string, ExperimentConfig> = {
  brandedInvestingContextualIgnorableModal: {
    id: 'reg-1846-branded-contextual-ign-val-prop-investing-urls',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow:
          REGISTRATION_MODALS.BRANDED_INVESTING_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V3_INVESTING,
      },
    },
    includeExcludeConfig: {
      verticalsToInclude: ['Investing'],
    },
  },
  compoundInterestCalculatorInvestingContextualIgnorable: {
    id: 'cad-697_cic_contextual_reg_investing',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow: REGISTRATION_MODALS.IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
      VARIANT: {
        id: 'variant',
        regFlow:
          REGISTRATION_MODALS.BRANDED_INVESTING_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V3_INVESTING,
      },
    },
    includeExcludeConfig: {
      urlsToInclude: ['/calculator/compound-interest-calculator'],
    },
  },
  sccRetirementCalculatorContextualIgnorable: {
    id: 'cad-749_contextual_reg_retirement',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow: REGISTRATION_MODALS.IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
      VARIANT: {
        id: 'variant',
        regFlow:
          REGISTRATION_MODALS.RETIREMENT_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V3_RETIREMENT,
      },
    },
    includeExcludeConfig: {
      urlsToInclude: ['/calculator/retirement-calculator'],
    },
  },
  otherRetirementCalcsContextualIgnorable: {
    id: 'cad-786_context_reg_retirement_other',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow: REGISTRATION_MODALS.IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
      VARIANT: {
        id: 'variant',
        regFlow:
          REGISTRATION_MODALS.RETIREMENT_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V3_RETIREMENT,
      },
    },
    includeExcludeConfig: {
      urlsToInclude: [
        '/investing/401k-calculator',
        '/investing/roth-ira-calculator',
      ],
    },
  },
  homeCalculatorsContextualIgnorable: {
    id: 'cad-747_contextual_reg_home',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow: REGISTRATION_MODALS.IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
      VARIANT: {
        id: 'variant',
        regFlow:
          REGISTRATION_MODALS.SMART_MORTGAGES_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V3_MORTGAGES,
      },
    },
    includeExcludeConfig: {
      urlsToInclude: [
        '/mortgages/how-much-house-can-i-afford',
        '/mortgages/mortgage-calculator',
        '/mortgages/rent-vs-buy-calculator',
        '/mortgages/amortization-schedule-calculator',
        '/mortgages/how-much-house-can-i-afford',
        '/mortgages/how-much-house-can-i-afford/calculate-affordability',
      ],
    },
  },
  colCalculatorContextualIgnorable: {
    id: 'cad-748_contextual_reg_col',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow: REGISTRATION_MODALS.IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
      VARIANT: {
        id: 'variant',
        regFlow:
          REGISTRATION_MODALS.RIGHT_MOVE_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V3_RIGHT_MOVE,
      },
    },
    includeExcludeConfig: {
      urlsToInclude: ['/cost-of-living-calculator'],
    },
  },
  mobileHeaderSigninDesignTest: {
    id: 'GROW-668_mobileheadernavregsignindesigntest_experiment',
    variants: {
      CONTROL: {
        id: 'control',
      },
      VARIANT: {
        id: 'treatment',
      },
    },
  },
  homepageRegistratonModalCopyTest: {
    id: 'EXPERT-1866_copy_and_background_reg_modal',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow: REGISTRATION_MODALS.IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
      VARIANT: {
        id: 'treatment_insights',
        regFlow:
          REGISTRATION_MODALS.INSIGHTS_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V3_INSIGHTS,
      },
      VARIANTB: {
        id: 'treatment_account',
        regFlow:
          REGISTRATION_MODALS.ACCOUNT_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V3_ACCOUNT,
      },
    },
    includeExcludeConfig: {
      urlsToInclude: ['/'],
    },
  },
};

/**
 * The order of these experiments in this array is important as
 * they will be used in the useContextualIgnorableModal hook to determine
 * which modal to configure. If multiple experiments are eligible the
 * experiment closest to the end will be used.
 */
export const CONTEXTUAL_IGNORABLE_MODAL_EXPERIMENTS = [
  EXPERIMENTS.brandedInvestingContextualIgnorableModal,
  EXPERIMENTS.sccRetirementCalculatorContextualIgnorable,
  EXPERIMENTS.otherRetirementCalcsContextualIgnorable,
  EXPERIMENTS.compoundInterestCalculatorInvestingContextualIgnorable,
  EXPERIMENTS.homeCalculatorsContextualIgnorable,
  EXPERIMENTS.colCalculatorContextualIgnorable,
  EXPERIMENTS.homepageRegistratonModalCopyTest,
];

/**
 * Experiments that will be fetched in fetch-async-props.js
 * These experiments should be accessed with useStoredExperiment
 */
export const EXPERIMENT_IDS_TO_FETCH = [
  EXPERIMENTS.mobileHeaderSigninDesignTest.id,
  EXPERIMENTS.sccRetirementCalculatorContextualIgnorable.id,
  EXPERIMENTS.otherRetirementCalcsContextualIgnorable.id,
  EXPERIMENTS.compoundInterestCalculatorInvestingContextualIgnorable.id,
  EXPERIMENTS.homeCalculatorsContextualIgnorable.id,
  EXPERIMENTS.colCalculatorContextualIgnorable.id,
  EXPERIMENTS.homepageRegistratonModalCopyTest.id,
];
